import { useMemo } from "react";
import { useCountry } from "@/contexts/CountryContext";

/**
 * Filter items by the globally-selected country, with a graceful fallback.
 *
 * Behaviour:
 *   • No country selected → returns `items` unchanged.
 *   • Country selected, matches found → returns just the matches (respects
 *     user's country intent).
 *   • Country selected, ZERO matches → returns the full `items` list so the
 *     user is never faced with an empty page just because their country
 *     doesn't have content for that section yet. Callers can consult the
 *     `.fallback` property on the returned array to display a soft note
 *     ("Not many in {country} — showing global content.").
 */
export function useCountryFilter(items, fields = ["country"]) {
  const { country } = useCountry();
  return useMemo(() => {
    if (!country || !Array.isArray(items) || items.length === 0) return items;
    const needle = country.name.toLowerCase();
    const matches = items.filter((it) => {
      if (!it) return false;
      for (const f of fields) {
        const parts = f.split(".");
        let v = it;
        for (const p of parts) v = v ? v[p] : undefined;
        if (typeof v === "string" && v.toLowerCase().includes(needle)) return true;
      }
      return false;
    });
    if (matches.length === 0) {
      // Fallback: expose full list but tag as `.fallback = true` so callers
      // can add a "showing global content" caption if they want.
      const out = [...items];
      out.fallback = true;
      return out;
    }
    return matches;
  }, [items, fields, country]);
}
